import { supabase } from "../../supabase/supabase";
import type {
  KnowledgeCollectionSchema,
  VoyageEmbeddingModel,
  VoyageRerankModel,
} from "@/types/knowledgeProviders";

export interface IntrospectResult {
  schema: KnowledgeCollectionSchema;
  description: string;
}

export interface TestSearchInput {
  collection: string;
  vectorName: string | null;
  embeddingModel: VoyageEmbeddingModel;
  query: string;
  limit?: number;
  scoreThreshold?: number | null;
  returnedFields?: string[];
  rerank?: boolean;
  rerankModel?: VoyageRerankModel;
}

export interface TestSearchHit {
  score: number;
  payload: Record<string, unknown>;
}

export interface TestSearchResult {
  results: TestSearchHit[];
  /** Set when reranking was asked for but failed, so the hits are in vector-score order. */
  note?: string;
}

// Calls the qdrant-collections edge function. It reads the org's Qdrant and Voyage
// credentials on the server, so no key reaches the browser.
async function invoke<T>(organizationId: string, action: string, params: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke("qdrant-collections", {
    body: { action, organization_id: organizationId, ...params },
  });

  if (error) {
    // A non-2xx answer carries the function's own { error } message in the response body.
    let message: string | undefined;
    try {
      const body = await (error as any).context?.json?.();
      message = body?.error;
    } catch {
      // keep the generic message
    }
    throw new Error(message || error.message || "Knowledge provider request failed");
  }
  return data as T;
}

export const knowledgeProvidersService = {
  async listCollections(organizationId: string): Promise<string[]> {
    const data = await invoke<{ collections: string[] }>(organizationId, "list");
    return data.collections ?? [];
  },

  introspect(organizationId: string, collection: string): Promise<IntrospectResult> {
    return invoke<IntrospectResult>(organizationId, "introspect", { collection });
  },

  async testSearch(organizationId: string, input: TestSearchInput): Promise<TestSearchResult> {
    const data = await invoke<TestSearchResult>(organizationId, "test_search", {
      collection: input.collection,
      vector_name: input.vectorName,
      embedding_model: input.embeddingModel,
      query: input.query,
      limit: input.limit,
      score_threshold: input.scoreThreshold ?? null,
      returned_fields: input.returnedFields ?? [],
      rerank: input.rerank ?? false,
      rerank_model: input.rerankModel,
    });
    return { results: data.results ?? [], note: data.note };
  },
};
